import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import {
  alpha,
  AppBar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  AgentView,
  Collections,
  createAuditNonce,
  ExportToCsv,
  Firebase,
  getSortedOptions,
  SearchCriteria,
  useApiContainer,
  useInfiniteData,
  useNotification,
} from '@ozark/common';
import {InputSearch, Loading, Title} from '@ozark/common/components';
import {MidAssociationResponse} from '@ozark/functions/src/functions/express/private/types/Applications';
import {format} from 'date-fns';
import {useCallback, useEffect, useState} from 'react';
import {AgentSelectDrawer} from './AgentSelectDrawer';

export const styles: Record<string, SxProps<Theme>> = {
  paper: {
    cursor: 'pointer',
    height: 40,
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    color: '#4d6575',
    padding: theme => theme.spacing(1, 2, 1, 0),
    margin: theme => theme.spacing(0, 0, 2, 0),
    width: '100%',
    transition: theme => theme.transitions.create(['padding', 'border-color', 'box-shadow']),
    duration: theme => theme.transitions.duration.complex,
    '&:hover': {
      boxShadow: theme => `0px 1px 3px 0px ${alpha(theme.palette.primary.light, 0.9)}`,
    },
  },
  divider: {
    padding: 0,
  },
  actionBar: {
    display: 'flex',
    padding: theme => theme.spacing(1.5, 2, 1.5, 0),
  },
  actionBarCallout: {
    padding: theme => theme.spacing(0.5, 2, 0.5, 0),
  },
  exportBtn: {
    padding: theme => theme.spacing(0.5, 2, 0.5, 0),
  },
  clearIcon: {
    padding: theme => theme.spacing(0.5, 0.5, 0.5, 0.5),
  },
  searchWrapper: {minWidth: '500px', marginRight: -1},
  checkboxWrapper: {height: '100%', minWidth: 40, paddingRight: 2, paddingLeft: 1},
  tableHeader: {
    height: 40,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    paddingY: 1,
    paddingRight: 2,
  },
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    column: {
      height: '100%',
      padding: theme.spacing(0, 2, 0, 2),
      flexBasis: '25%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      transition: theme.transitions.create(['flexBasis'], {
        duration: theme.transitions.duration.standard,
      }),
      [theme.breakpoints.down('lg')]: {
        '&:hover': {
          flexBasis: '40%',
        },
      },
    },
  })
);

const getAgentName = (agent: AgentView) => `${agent.firstName} ${agent.lastName}`;

type OrderBy = {
  orderBy: keyof MidAssociationResponse;
  order: 'asc' | 'desc';
};

type Props = {
  agents?: AgentView[];
};

export const AgentMidAssociation = ({agents}: Props) => {
  const classes = useStyles();
  const apiClient = useApiContainer();
  const showNotification = useNotification();

  const [updatingMids, setUpdatingMids] = useState(false);
  const [activeAgents, setActiveAgents] = useState<Record<string, AgentView>>({});

  const [checkedMids, setCheckedMids] = useState<{
    [_key: string]: {checked: boolean; appId: string; agentId?: string};
  }>({});
  const [assignDialogOpen, setAssignedDialogOpen] = useState(false);
  const [options, setOptions] = useState<OrderBy>({orderBy: 'dispositionUpdatedAt', order: 'desc'});
  const [associationsData, setAssociationsData] = useState<MidAssociationResponse[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [search, setSearch] = useState('');
  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState<boolean>(true);

  const getAssociations = async (page: number) => {
    setLoading(true);
    const cancelSource = apiClient?.applications.getCancelTokenSource();
    try {
      const result = await apiClient?.applications.getAgentMidAssociations(
        {limit: 20, offset: page, ...options} as SearchCriteria,
        search,
        cancelSource?.token
      );

      if (!result) {
        throw new Error('getAgentMidAssociations result is null or undefined');
      }
      const associations = page === 1 ? [...result.data] : [...associationsData, ...result.data];

      setAssociationsData(associations);

      setPageNum(page);
      setHasMore(associations.length < result.totalCount);
    } catch (error) {
      showNotification('error', 'Failed to load data');
      console.error('Failed to load agent mid associations data with an error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAssociationsData([]);
    getAssociations(1);
  }, [search, options]);

  const onLoadMore = () => {
    if (loading) {
      return;
    }
    getAssociations(pageNum + 1);
  };

  const loadMoreRef = useInfiniteData(onLoadMore, hasMore);

  useEffect(() => {
    if (!agents) {
      return;
    }

    setActiveAgents(
      getSortedOptions(agents, getAgentName)!.reduce((accumulator, agent) => {
        if (agent.isActive) {
          accumulator[agent.id] = agent;
        }
        return accumulator;
      }, {} as Record<string, AgentView>)
    );
  }, [agents, setActiveAgents]);

  const handleMidCheck = (mid: string, appId: string, agentId?: string) => (_event: any) => {
    setCheckedMids(prevState => ({
      ...prevState,
      [mid]: {checked: !prevState[mid] || !prevState[mid].checked, appId, agentId},
    }));
  };

  const handleAssignMids = () => {
    setAssignedDialogOpen(true);
  };

  const handleClearCheckedMids = () => {
    setCheckedMids({});
  };

  const handleCloseAssignDialog = () => {
    setAssignedDialogOpen(false);
  };

  const handleAgentAssign = (agentId: string) => {
    setUpdatingMids(true);

    // Unless there is a programming error there should always be agent, therefore selecting first item is safe.
    updateFirestore(activeAgents[agentId]);

    setCheckedMids({});
    setUpdatingMids(false);
  };

  const updateFirestore = async (agent: AgentView) => {
    const batch = Firebase.firestore.batch();

    const auditNonce = createAuditNonce(Firebase.auth.currentUser!.uid);

    for (let mid of Object.keys(checkedMids)) {
      if (
        checkedMids[mid].checked &&
        (!checkedMids[mid].agentId || checkedMids[mid].agentId !== agent.id)
      ) {
        var appRef = Firebase.firestore
          .collection(Collections.applications)
          .doc(checkedMids[mid].appId);

        const data = {
          'agent.id': agent.id,
          'agent.firstName': agent.firstName,
          'agent.lastName': agent.lastName,
          auditNonce,
        };

        batch.update(appRef, data);
      }
    }

    try {
      await batch.commit();
      showNotification('success', 'Successfully assigned the new agent');
    } catch (err: any) {
      setUpdatingMids(false);
      console.error(err);
      showNotification('error', (err as Error).message);
    }
  };

  const exportToCsv = async () => {
    if (!apiClient) return [];

    const exportOptions = {
      fieldSeparator: ',',
      filename: `mid-associations-report`,
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: false,
      title: 'Agent MID Associations Report',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
    };
    const cancelSource = apiClient.applications.getCancelTokenSource();

    const report = await apiClient.applications.getAgentMidAssociations(
      {limit: 0, offset: 0, ...options} as SearchCriteria,
      search,
      cancelSource?.token
    );

    const data =
      report?.data.map(x => ({
        boardedDate: format(new Date(x.dispositionUpdatedAt), 'MM/dd/yyyy h:mm a'),
        mid: x.mid.toString(),
        doingBusinessAs: x.dba,
        legalBusinessName: x.legalName,
        assignedAgent: x.agentName,
        groupName: x.groupName,
      })) ?? [];

    if (data.length === 0) {
      showNotification('info', 'There are no records to export');
      return;
    }

    const exporter = new ExportToCsv(exportOptions);

    exporter.generateCsv(data);
  };

  const getNumberOfSelectedMids = () => {
    return Object.keys(checkedMids).reduce((accumulator, value) => {
      return accumulator + Number(checkedMids[value].checked);
    }, 0);
  };

  const onColumnHeaderClick = (property: keyof MidAssociationResponse) => () => {
    const order = property === options.orderBy ? (options.order === 'asc' ? 'desc' : 'asc') : 'asc';
    setOptions({orderBy: property, order});
  };

  const handleSearchChange = useCallback((search: string) => setSearch(search), [setSearch]);

  const columnHeader = (title: string, property: keyof MidAssociationResponse) => {
    const isSortedByField = property === options.orderBy;

    return (
      <div className={classes.column}>
        <Typography
          variant="subtitle1"
          color={isSortedByField ? 'primary' : 'inherit'}
          component="p"
          noWrap
          gutterBottom
          onClick={onColumnHeaderClick(property)}
          sx={{cursor: 'pointer'}}
        >
          {`${title} `}
          {isSortedByField &&
            (options.order === 'desc' ? (
              <ArrowDownwardIcon fontSize={'small'} sx={{marginBottom: '-4px'}} />
            ) : (
              <ArrowUpwardIcon fontSize={'small'} sx={{marginBottom: '-4px'}} />
            ))}
        </Typography>
      </div>
    );
  };

  const renderSearch = () => (
    <Box sx={styles.searchWrapper}>
      <InputSearch
        fieldName="searchMerchants"
        placeholder="Search by MID, DBA, Legal Name or Agent Name..."
        onSearchChange={handleSearchChange}
      />
    </Box>
  );

  const numberOfSelectedMids = getNumberOfSelectedMids();

  return (
    <Box sx={{height: '100%', minHeight: '100%', display: 'flex', flexDirection: 'column'}}>
      <Title
        breadcrumbs={[
          <Link component="button" variant="body1">
            Agent/MID Associations
          </Link>,
        ]}
      >
        {numberOfSelectedMids > 0 && (
          <Box sx={styles.actionBar}>
            <IconButton
              sx={styles.clearIcon}
              color="primary"
              aria-label="upload picture"
              component="span"
              onClick={handleClearCheckedMids}
              size="large"
            >
              <ClearIcon />
            </IconButton>
            <Typography sx={styles.actionBarCallout}>
              {numberOfSelectedMids} MID{numberOfSelectedMids === 1 ? '' : 's'} selected
            </Typography>
            <Button
              size="small"
              onClick={handleAssignMids}
              variant="contained"
              color="primary"
              startIcon={<PersonIcon />}
            >
              Assign
            </Button>
          </Box>
        )}

        <Box sx={styles.exportBtn}>
          <Button
            size="small"
            onClick={exportToCsv}
            variant="contained"
            color="primary"
            startIcon={<DescriptionIcon />}
          >
            Export
          </Button>
        </Box>
        {agents && <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>{renderSearch()}</Box>}
      </Title>
      <AppBar sx={{position: 'sticky', top: '65px', marginBottom: 1.5}} elevation={0}>
        <Paper sx={styles.tableHeader}>
          <Box sx={{height: '100%', minWidth: 40, paddingRight: 2, paddingLeft: 1}} />
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          {columnHeader('Boarded Date', 'dispositionUpdatedAt')}
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          {columnHeader('MID', 'mid')}
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          {columnHeader('Doing Business As', 'dba')}
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          {columnHeader('Legal Business Name', 'legalName')}
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          {columnHeader('Assigned Agent', 'agentName')}
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          {columnHeader('Group', 'groupName')}
        </Paper>
      </AppBar>

      {associationsData.map((document, i) => (
        <Paper
          sx={styles.paper}
          onClick={handleMidCheck(document.mid!, document.id, document.agentId)}
          ref={associationsData.length - 1 === i ? loadMoreRef : null}
        >
          <Box sx={styles.checkboxWrapper}>
            <Checkbox
              checked={
                checkedMids[document.mid!] && checkedMids[document.mid!].checked ? true : false
              }
              name="checkedB"
              color="primary"
              sx={{padding: 0}}
            />
          </Box>
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          <Box className={classes.column}>
            <Typography variant="body1" component="p" noWrap gutterBottom color="textPrimary">
              {document.dispositionUpdatedAt
                ? format(new Date(document.dispositionUpdatedAt), 'MM/dd/yyyy h:mm a')
                : 'n/a'}
            </Typography>
          </Box>
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          <Box className={classes.column}>
            <Typography variant="body1" component="p" noWrap gutterBottom color="textPrimary">
              {document.mid}
            </Typography>
          </Box>
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          <Box className={classes.column}>
            <Typography variant="body1" component="p" noWrap gutterBottom color="textPrimary">
              {document.dba}
            </Typography>
          </Box>
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          <Box className={classes.column}>
            <Typography variant="body1" component="p" noWrap gutterBottom color="textPrimary">
              {document.legalName}
            </Typography>
          </Box>
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          <Box className={classes.column}>
            <Typography variant="body1" component="p" noWrap gutterBottom color="textPrimary">
              {document.agentName}
            </Typography>
          </Box>
          <Divider sx={styles.divider} orientation="vertical" flexItem />
          <Box className={classes.column}>
            <Typography variant="body1" component="p" noWrap gutterBottom color="textPrimary">
              {document.groupName}
            </Typography>
          </Box>
        </Paper>
      ))}
      {loading && (
        <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
          <CircularProgress color="primary" />
        </Box>
      )}
      <AgentSelectDrawer
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        agents={Object.keys(activeAgents).map(agentId => activeAgents[agentId])}
        onAgentSelect={handleAgentAssign}
      />
      {updatingMids && <Loading />}
    </Box>
  );
};
